import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDocs,
  getFirestore,
  Timestamp,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useWorkspaceContext, workspacesEnabled } from 'ui';
import { sharingV2Enabled, sharingV2Known, useSharingV2Store } from 'ui/utils/workspace/sharingV2';
import { useSharingV2 } from 'ui/utils/useDesignFirm';
import { firebaseHelper } from 'ui/utils/firebase';
import { teamScopeConstraints, type TeamScope } from '../v2/apis/accessQueries';
import { firebaseApp } from '../libs/firebase';
import { useLoginState } from '../studioxr/auth/LoginState';
import type {
  Artifact,
  Concept,
  ConceptVersion,
  Message,
  AddMessageInput,
  AddMessageResponse,
} from '@mattoboard/schemas';
import { CHAT_GRAPH_TIMEOUT_MS } from '@mattoboard/schemas';
import { getAddMessageUrl } from '../design_stream/components/apiConfig';
import { copyVersionMetadata } from '../design_stream_evt/utils';
import { fileActionOf, teamAnalytics, teamItem } from '../team/teamAnalytics';
import { sendExceptionToSentry } from '../libs/sentry';
import { useCreateConceptWithAddMessage } from './useCreateConcept';

/**
 * Get Firebase services lazily (after firebaseApp is initialized)
 */
function getFirebaseServices() {
  if (!firebaseApp) {
    return null;
  }
  return {
    db: getFirestore(firebaseApp),
    auth: getAuth(firebaseApp),
  };
}

/**
 * Hook to get the current user ID with auth state changes.
 */
function useCurrentUserId() {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      return;
    }

    // Set initial value
    setUserId(services.auth.currentUser?.uid ?? null);

    const unsubscribe = onAuthStateChanged(services.auth, (user) => {
      setUserId(user?.uid ?? null);
    });
    return () => unsubscribe();
  }, []);

  return userId;
}

/**
 * Convert Firestore timestamp to milliseconds.
 * Handles Firestore Timestamp objects, serialized forms, and raw numbers.
 */
export function getTimestampMs(timestamp: unknown): number {
  if (!timestamp) return Date.now();
  if (typeof (timestamp as any).toMillis === 'function') {
    return (timestamp as any).toMillis();
  }
  if (typeof (timestamp as any).seconds === 'number') {
    return (timestamp as any).seconds * 1000;
  }
  if (typeof timestamp === 'number') {
    return timestamp;
  }
  return Date.now();
}

// Frontend stuck-pending threshold. Sourced from @mattoboard/schemas so the
// backend graph.invoke timeout and frontend detection cannot disagree (see
// issue #3142 — a 50s frontend timeout was firing while the backend was still
// successfully completing).
export const STUCK_PENDING_TIMEOUT_MS = CHAT_GRAPH_TIMEOUT_MS;
const CONCEPTS_WINDOW_SIZE = 100;
// Sort by updated_at so recently-edited concepts surface to the top.
//
// History: commit 45d19bbf originally introduced this; a regression was observed
// where week-old concepts appeared above newly-created ones because the old
// fire-and-forget precomputeSegments() wrote SEGMENT_CACHE versions asynchronously,
// racing with updated_at on newer concepts. That race is eliminated by commit
// 6e300d625, which inlines segment computation into LLM_GENERATION so no separate
// Firestore write occurs. updated_at is now a clean signal of user activity.
//
// DO NOT change this to 'created_at': that would break "recently edited" ordering,
// where editing an old concept should bring it to the top of the sidebar.
export const CONCEPTS_LIST_SORT_FIELD = 'updated_at' as const;

/**
 * Workspace ownership check for a concept (Phase A.3.1).
 *
 * Concepts created after the workspace migration carry a `design_firm_id`
 * stamped by addMessageInternal. Pre-migration concepts have no such field —
 * those default to Personal (matching Option B: existing content stays in
 * the user's Personal workspace).
 *
 * Team workspace: only concepts where design_firm_id === current firm id.
 * Personal workspace: only concepts with no design_firm_id (or explicit null).
 */
export function conceptBelongsToWorkspace(
  concept: Concept,
  activeWorkspace: { kind: 'personal' } | { kind: 'team'; firmId: string },
): boolean {
  const cFirmId = (concept as any).design_firm_id ?? null;
  if (activeWorkspace.kind === 'team') {
    return cFirmId === activeWorkspace.firmId;
  }
  return cFirmId == null;
}
const MESSAGE_WINDOW_SIZE = 100;
const ERROR_MESSAGE_SCAN_LIMIT = 50;

/**
 * Build the concept messages query so we always fetch the latest message window.
 */
export function buildConceptMessagesQuery(db: Firestore, conceptId: string) {
  const messagesRef = collection(db, 'concepts', conceptId, 'messages');
  return query(messagesRef, orderBy('created_at', 'desc'), limit(MESSAGE_WINDOW_SIZE));
}

export function buildConceptErrorMessagesQuery(db: Firestore, conceptId: string) {
  const messagesRef = collection(db, 'concepts', conceptId, 'messages');
  return query(
    messagesRef,
    orderBy('created_at', 'desc'),
    limit(ERROR_MESSAGE_SCAN_LIMIT),
  );
}

/**
 * Build the Sentry `context` payload for a "Concept generation failed" event.
 * Enriches the previously-opaque telemetry (MATTOBOARD-6NV) with the actual
 * error_message / error_code populated on the failed Message by the Express
 * backend's persistErrorMessage helper.
 */
export function buildConceptErrorContext(
  concept: Concept,
  latestFailedMessage: Message | null,
) {
  return {
    conceptId: concept.id,
    name: concept.current?.name ?? null,
    errorMessage: latestFailedMessage?.error_message ?? null,
    errorCode: latestFailedMessage?.error_code ?? null,
    messageState: concept.message_state ?? null,
    messageId: latestFailedMessage?.id ?? null,
  };
}

function buildConceptVersionsQuery(db: Firestore, conceptId: string) {
  const versionsRef = collection(db, 'concepts', conceptId, 'versions');
  return query(versionsRef, orderBy('created_at', 'desc'), limit(MESSAGE_WINDOW_SIZE));
}

/**
 * Convert latest-first message snapshots to chronological order for rendering.
 */
export function normalizeRecentMessagesForDisplay(
  messages: Message[],
  windowSize: number = MESSAGE_WINDOW_SIZE,
): Message[] {
  return messages.slice(0, windowSize).reverse();
}

/** Is a message actively being processed? Falls back to legacy current.state for pre-migration docs. */
export function isBusy(concept: Concept): boolean {
  if (concept.is_busy !== undefined) return concept.is_busy;
  return concept.current?.state === 'PENDING'; // legacy fallback
}

/** Is the concept in an error state? Falls back to legacy current.state for pre-migration docs. */
export function isError(concept: Concept): boolean {
  if (concept.message_state)
    return concept.message_state === 'FAILED' || concept.message_state === 'CANCELLED';
  return concept.current?.state === 'ERROR'; // legacy fallback
}

/** Derive a display-friendly state string. New docs use message_state/is_busy; old docs use current.state. */
export function getDisplayState(concept: Concept): string | undefined {
  // A board-redirect concept is a resolved routing artifact (it produced a
  // separate board project). It's normally filtered out of the list entirely,
  // but guard here so a stale/unfiltered list can never render it as an error.
  if (concept.current?.action_type === 'BOARD_GENERATE') return 'COMPLETED';
  // New docs: derive from message_state/is_busy
  if (concept.message_state !== undefined) {
    if (isBusy(concept)) return 'PENDING';
    // If a render exists, treat the concept card as completed even if an old
    // FAILED/CANCELLED message_state lingered from a prior attempt.
    if (concept.current?.img_url && concept.current?.action_type !== 'ARCHIVE') {
      return 'COMPLETED';
    }
    if (isError(concept)) return 'ERROR';
    return 'COMPLETED';
  }
  // Legacy docs: map FINISHED → COMPLETED
  if (concept.current?.state === 'FINISHED') return 'COMPLETED';
  return concept.current?.state;
}

/** Committed render, else draft-variant thumbnail (backend-stamped thumbnail_url, or a client fallback for pre-#3363 drafts). */
export function getConceptThumbnail(concept: Concept, thumbnailFallback?: string): string | undefined {
  return concept.current?.img_url ?? concept.thumbnail_url ?? thumbnailFallback;
}

/**
 * Apply concept list visibility based on archive preference.
 */
export function applyConceptArchiveFilter(
  concepts: Concept[],
  includeArchived: boolean = false,
): Concept[] {
  if (includeArchived) return concepts;
  return concepts.filter(
    (c) =>
      c.current?.action_type !== 'ARCHIVE' &&
      c.current?.action_type !== 'BOARD_GENERATE' &&
      c.current?.state !== 'ARCHIVED',
  );
}

/**
 * Extract the first output text value from a message content payload.
 */
export function extractMessageOutputText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';
  const textBlock = content.find(
    (b: any) => b?.type === 'output_text' && typeof b?.text === 'string',
  ) as { text?: string } | undefined;
  return textBlock?.text || '';
}

interface SubcollectionHookResult<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
}

function identityItems<T>(items: T[]): T[] {
  return items;
}

/**
 * Generic realtime hook for concept subcollections (e.g. versions, messages).
 */
function useConceptSubcollection<T>(
  conceptId: string | null,
  buildQueryForConcept: (db: Firestore, conceptId: string) => any,
  normalize: (items: T[]) => T[] = identityItems,
): SubcollectionHookResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setLoading(false);
      return;
    }

    if (!conceptId) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = buildQueryForConcept(services.db, conceptId);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as T[];
        setItems(normalize(nextItems));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching concept subcollection:', err);
        setError(err);
        setItems([]); // access lost: drop what this listener showed
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [conceptId, buildQueryForConcept, normalize]);

  return { items, loading, error };
}

/**
 * Check if the concept is actively pending (current state is PENDING, created < 5 minutes ago).
 */
export function hasActivePendingVersion(
  concept: Concept,
  now: number = Date.now()
): boolean {
  if (!isBusy(concept)) return false;
  return now - getTimestampMs(concept.current?.created_at ?? concept.updated_at ?? concept.created_at) < STUCK_PENDING_TIMEOUT_MS;
}

/**
 * Filter out PENDING concepts older than the stuck-pending threshold.
 */
export function filterStuckPending(concepts: Concept[]): Concept[] {
  const now = Date.now();
  return concepts.filter((c) => {
    if (!isBusy(c)) return true;
    if (c.current?.img_url) return true;
    return now - getTimestampMs(c.created_at) < STUCK_PENDING_TIMEOUT_MS;
  });
}

/**
 * Check if a follow-up edit is pending.
 * Used to show "Generating your edit…" on concept pages that already have a finished image.
 * A follow-up is pending when concept.current.state is PENDING AND a previous image exists.
 */
export function hasFollowUpPending(
  concept: Concept,
  versions: ConceptVersion[],
  now: number = Date.now(),
): boolean {
  if (!isBusy(concept)) return false;
  if (now - getTimestampMs(concept.current?.created_at ?? concept.updated_at ?? concept.created_at) >= STUCK_PENDING_TIMEOUT_MS) return false;
  // A "follow-up" means there's a previous finished image
  return versions.some((v) => v.img_url && v.action_type !== 'SEGMENT_CACHE');
}

/**
 * Check if a concept is stuck in PENDING state (created >= 5 minutes ago).
 * Used to detect concepts that need client-side recovery to ERROR state.
 */
export function isStuckPending(concept: Concept, now: number = Date.now()): boolean {
  if (!isBusy(concept)) return false;
  const timestamp = concept.current?.created_at ?? concept.updated_at ?? concept.created_at;
  return now - getTimestampMs(timestamp) >= STUCK_PENDING_TIMEOUT_MS;
}

/**
 * Check if the last message is in a blocking state (PENDING or PROCESSING)
 * and was created less than 5 minutes ago.
 * Pre-migration messages without state are treated as non-blocking.
 */
export function isLastMessagePending(
  messages: Message[],
  now: number = Date.now(),
  timeoutMs: number = STUCK_PENDING_TIMEOUT_MS,
): boolean {
  if (!messages.length) return false;
  const last = messages[messages.length - 1];
  if (last.state !== 'PENDING' && last.state !== 'PROCESSING') return false;
  return now - getTimestampMs(last.created_at) < timeoutMs;
}

/**
 * Check if the last message has been stuck in PENDING/PROCESSING >= 5 minutes.
 */
export function isLastMessageStuckPending(
  messages: Message[],
  now: number = Date.now(),
): boolean {
  if (!messages.length) return false;
  const last = messages[messages.length - 1];
  if (last.state !== 'PENDING' && last.state !== 'PROCESSING') return false;
  return now - getTimestampMs(last.created_at) >= STUCK_PENDING_TIMEOUT_MS;
}

/**
 * Check if the last message indicates a terminal error.
 */
export function isLastMessageFailed(messages: Message[]): boolean {
  if (!messages.length) return false;
  const last = messages[messages.length - 1];
  return last.state === 'FAILED' || last.state === 'CANCELLED';
}

/**
 * Hook to list all concepts for the current user with realtime updates.
 */
type WorkspaceFilter = { kind: 'team'; firmId: string } | { kind: 'personal' };

// Teams sharing (sharingV2): in a flagged team workspace the list is the
// concepts the user can read, not every team-stamped concept. Undefined while
// the team's flag is still loading on a cold load, so the first query waits
// for its shape (a failed firm read settles it too).
function useConceptTeamScope(
  userId: string | null,
  workspaceFilter: WorkspaceFilter,
): TeamScope | null | undefined {
  const firmId = workspaceFilter.kind === 'team' ? workspaceFilter.firmId : null;
  const flag = useSharingV2Store((s) => (firmId ? s.flags[firmId] : undefined));
  const { loading } = useSharingV2(firebaseHelper.db, firmId);
  const waiting = !!firmId && !sharingV2Known(firmId) && loading;
  return useMemo(
    () =>
      waiting
        ? undefined
        : firmId && userId && sharingV2Enabled(firmId)
          ? { uid: userId, firmId }
          : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firmId, userId, flag, waiting],
  );
}

const workspaceScopeClause = (
  workspaceFilter: WorkspaceFilter,
  userId: string,
  teamScope: TeamScope | null,
): QueryConstraint[] => {
  if (teamScope) return teamScopeConstraints(teamScope);
  // Every team runs the sharing model, so a team workspace always reads by its
  // readers; Personal is owner-only.
  return workspaceFilter.kind === 'team'
    ? teamScopeConstraints({ uid: userId, firmId: workspaceFilter.firmId })
    : [where('owner_id', '==', userId)];
};

export function useConcepts(options?: { includeArchived?: boolean }) {
  const userId = useCurrentUserId();
  const permissions = useLoginState((s) => s.permissions);
  const workspace = useWorkspaceContext(userId, permissions);
  const workspaceFilter = useMemo(
    () =>
      workspace.active.kind === 'team'
        ? ({ kind: 'team', firmId: workspace.active.firmId } as const)
        : ({ kind: 'personal' } as const),
    [workspace.active.kind, workspace.active.id],
  );
  const teamScope = useConceptTeamScope(userId, workspaceFilter);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const includeArchived = options?.includeArchived ?? false;

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setLoading(false);
      return;
    }

    if (!userId) {
      setConcepts([]);
      setLoading(false);
      return;
    }
    if (teamScope === undefined) return; // the team's flag is still loading

    setLoading(true);
    setError(null);

    // Only report errors that happen in real-time (not historical ones on initial load)
    const reportedErrors = new Set<string>();
    let isInitialSnapshot = true;

    // Team workspace: fetch every concept stamped with the firm id (all
    // members' team concepts), not just the caller's own. Personal: unchanged
    // owner-scoped query. The conceptBelongsToWorkspace filter below stays as
    // a defensive belt. Firestore rules enforce read access (see
    // firestore.rules — concepts team-member read).
    const conceptsRef = collection(services.db, 'concepts');
    const q = query(
      conceptsRef,
      ...workspaceScopeClause(workspaceFilter, userId, teamScope),
      orderBy(CONCEPTS_LIST_SORT_FIELD, 'desc'),
      limit(CONCEPTS_WINDOW_SIZE)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conceptsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Concept[];

        // Report concept errors to Sentry only for real-time changes (skip initial load)
        if (!isInitialSnapshot) {
          for (const change of snapshot.docChanges()) {
            if (change.type !== 'added' && change.type !== 'modified') continue;
            const c = { id: change.doc.id, ...change.doc.data() } as Concept;
            if (getDisplayState(c) === 'ERROR' && !reportedErrors.has(c.id)) {
              reportedErrors.add(c.id);
              // Best-effort fetch of the latest failed message to enrich the Sentry
              // payload with error_message / error_code (MATTOBOARD-6NV).
              void (async () => {
                let failedMessage: Message | null = null;
                try {
                  const snap = await getDocs(
                    buildConceptErrorMessagesQuery(services.db, c.id),
                  );
                  for (const d of snap.docs) {
                    const m = { id: d.id, ...d.data() } as Message;
                    if (m.state === 'FAILED' || m.state === 'CANCELLED') {
                      failedMessage = m;
                      break;
                    }
                  }
                } catch {
                  // best-effort enrichment; fall through and still report
                }
                sendExceptionToSentry({
                  error: new Error(`Concept generation failed: ${c.id}`),
                  contextName: 'concept_error',
                  context: buildConceptErrorContext(c, failedMessage),
                  tags: {
                    concept_id: c.id,
                    ...(failedMessage?.error_code
                      ? { error_code: failedMessage.error_code }
                      : {}),
                  },
                });
              })();
            }
          }
        }
        isInitialSnapshot = false;

        // Filter by active workspace (Phase A.3.1) BEFORE archive filter so
        // archived/active counts in the UI reflect the user's current
        // workspace. When workspaces are gated in production credits still
        // route to the team wallet (see useCreditWorkspace), so a team
        // designer's new concepts are team-stamped — show all of the owner's
        // concepts regardless of stamp (pre-workspaces, owner-scoped behavior)
        // rather than dropping the team-stamped ones from their Personal list.
        const workspaceScoped = workspacesEnabled()
          ? conceptsList.filter((c) =>
              conceptBelongsToWorkspace(c, workspaceFilter),
            )
          : conceptsList;
        setConcepts(applyConceptArchiveFilter(workspaceScoped, includeArchived));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching concepts:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, includeArchived, workspaceFilter, teamScope]);

  return { concepts, loading, error };
}

const DEFAULT_PAGINATED_PAGE_SIZE = 24;

/**
 * Merge a project's associated concepts (from the targeted query below) with
 * a general concepts list, deduped by id with the associated concepts first.
 * Used by the Add Existing Concepts picker so already-associated concepts are
 * always present (and pre-checked) regardless of how deep the user has paged.
 *
 * Exported for unit testing.
 */
export function mergeProjectPinnedConcepts(
  pinned: Concept[],
  rest: Concept[],
): Concept[] {
  return concatDedupeById(pinned, rest);
}

/**
 * Live listener for the concepts associated with a specific project,
 * independent of recency (#3670). The windowed hooks above only see a
 * concept's project association if it falls inside the 100-most-recent
 * window; this queries `current.project_id` directly.
 *
 * Permission shape: the concepts `list` rule requires every query to be
 * provably owner- or firm-scoped, so the project clause rides on top of the
 * same owner_id / design_firm_id clause the other hooks use. Requires the
 * composite indexes (owner_id, current.project_id, updated_at) and
 * (design_firm_id, current.project_id, updated_at) — see
 * firebase/firestore.indexes.<env>.json.
 *
 * Degrades gracefully: on query error (e.g. index not yet deployed) it
 * reports the error and an empty list, so callers that union with the
 * windowed list keep the pre-#3670 behavior instead of breaking.
 */
export function useProjectConcepts(
  projectId: string | null,
  options?: { includeArchived?: boolean },
) {
  const userId = useCurrentUserId();
  const permissions = useLoginState((s) => s.permissions);
  const workspace = useWorkspaceContext(userId, permissions);
  const workspaceFilter = useMemo(
    () =>
      workspace.active.kind === 'team'
        ? ({ kind: 'team', firmId: workspace.active.firmId } as const)
        : ({ kind: 'personal' } as const),
    [workspace.active.kind, workspace.active.id],
  );
  const teamScope = useConceptTeamScope(userId, workspaceFilter);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const includeArchived = options?.includeArchived ?? false;

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setLoading(false);
      return;
    }

    if (!userId || !projectId) {
      setConcepts([]);
      setLoading(false);
      return;
    }
    if (teamScope === undefined) return; // the team's flag is still loading

    setLoading(true);
    setError(null);

    const conceptsRef = collection(services.db, 'concepts');
    const q = query(
      conceptsRef,
      ...workspaceScopeClause(workspaceFilter, userId, teamScope),
      where('current.project_id', '==', projectId),
      orderBy(CONCEPTS_LIST_SORT_FIELD, 'desc'),
      limit(CONCEPTS_WINDOW_SIZE),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conceptsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Concept[];
        // Same defensive workspace scoping as useConcepts (and same gated-mode
        // pass-through: when workspaces are hidden, show all owner concepts
        // regardless of stamp).
        const workspaceScoped = workspacesEnabled()
          ? conceptsList.filter((c) =>
              conceptBelongsToWorkspace(c, workspaceFilter),
            )
          : conceptsList;
        setConcepts(applyConceptArchiveFilter(workspaceScoped, includeArchived));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching project concepts:', err);
        setError(err);
        setConcepts([]);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [userId, projectId, includeArchived, workspaceFilter, teamScope]);

  return { concepts, loading, error };
}

type FirestoreTimestampLike = { seconds: number; nanoseconds?: number };

/**
 * Read the `updated_at` field as a Firestore-comparable cursor value.
 * Firestore compares Timestamps, so we prefer those when available and
 * fall back to a JS Date otherwise.
 */
export function readUpdatedAtCursor(concept: Concept): Timestamp | Date | null {
  const updatedAt = concept?.updated_at as
    | Timestamp
    | FirestoreTimestampLike
    | number
    | null
    | undefined;
  if (updatedAt == null) return null;
  if (updatedAt instanceof Timestamp) return updatedAt;
  if (typeof updatedAt === 'number') return new Date(updatedAt);
  if (typeof updatedAt === 'object' && typeof updatedAt.seconds === 'number') {
    return new Timestamp(updatedAt.seconds, updatedAt.nanoseconds ?? 0);
  }
  return null;
}

function concatDedupeById<T extends { id?: string }>(...lists: T[][]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const list of lists) {
    for (const item of list) {
      if (item.id == null) continue;
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/**
 * Merge live first-page concepts with paged (one-time) concepts.
 * First page wins on duplicate id (it has the freshest data). The merged
 * list is re-sorted by updated_at DESC so bumped paged concepts take their
 * new position, then filtered for archive.
 *
 * Exported for unit testing.
 */
export function mergePaginatedConcepts(
  firstPage: Concept[],
  paged: Concept[],
  includeArchived: boolean,
): Concept[] {
  const merged = concatDedupeById(firstPage, paged);
  merged.sort(
    (a, b) => getTimestampMs(b.updated_at) - getTimestampMs(a.updated_at),
  );
  return applyConceptArchiveFilter(merged, includeArchived);
}

/**
 * Where-clauses shared by the paginated list's page-1 and loadMore queries.
 * Team workspace: every concept stamped with the firm id; personal: the
 * owner's. `source` (Image Edit list) needs the
 * (owner_id|design_firm_id, source, updated_at) composite indexes.
 */
function conceptsListScope(
  workspaceFilter: WorkspaceFilter,
  userId: string,
  source?: string,
  teamScope: TeamScope | null = null,
): QueryConstraint[] {
  return [
    ...workspaceScopeClause(workspaceFilter, userId, teamScope),
    ...(source ? [where('source', '==', source)] : []),
  ];
}

/**
 * Paginated + realtime hook for the concepts list.
 *
 * Page 1 uses onSnapshot so newly-created concepts and state transitions
 * (PENDING → COMPLETED/ERROR, archive) update live. Pages 2+ are one-time
 * getDocs() reads keyed by an `updated_at` timestamp cursor from the live
 * page-1 tail. Using a timestamp cursor (rather than startAfter(DocSnap))
 * prevents gaps when a paged concept's updated_at bumps into page 1.
 */
export function useConceptsPaginated(options?: {
  pageSize?: number;
  includeArchived?: boolean;
  /** Only concepts whose `source` equals this (e.g. "UPLOAD" for Image Edit). */
  source?: string;
}) {
  const userId = useCurrentUserId();
  const permissions = useLoginState((s) => s.permissions);
  const workspace = useWorkspaceContext(userId, permissions);
  const workspaceFilter = useMemo(
    () =>
      workspace.active.kind === 'team'
        ? ({ kind: 'team', firmId: workspace.active.firmId } as const)
        : ({ kind: 'personal' } as const),
    [workspace.active.kind, workspace.active.id],
  );
  const teamScope = useConceptTeamScope(userId, workspaceFilter);
  const pageSize = options?.pageSize ?? DEFAULT_PAGINATED_PAGE_SIZE;
  const includeArchived = options?.includeArchived ?? false;
  const source = options?.source;

  const [firstPage, setFirstPage] = useState<Concept[]>([]);
  const [paged, setPaged] = useState<Concept[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Oldest updated_at we've paged past. Once set, it's always older than
  // the live page-1 tail, so loadMore prefers it. Tracking this in a ref
  // (rather than deriving from state) prevents the live listener's growing
  // tail from resetting paged progress.
  const pagedTailCursorRef = useRef<Timestamp | Date | null>(null);
  // Mirrors firstPage's oldest updated_at so loadMore can read it without
  // firstPage being a dep (which would recreate loadMore on every tick).
  const firstPageTailCursorRef = useRef<Timestamp | Date | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setLoading(false);
      return;
    }

    if (!userId) {
      setFirstPage([]);
      setPaged([]);
      setHasMore(true);
      setLoading(false);
      pagedTailCursorRef.current = null;
      firstPageTailCursorRef.current = null;
      return;
    }
    if (teamScope === undefined) return; // the team's flag is still loading

    setLoading(true);
    setError(null);
    setFirstPage([]);
    setPaged([]);
    setHasMore(true);
    pagedTailCursorRef.current = null;
    firstPageTailCursorRef.current = null;

    const reportedErrors = new Set<string>();
    let isInitialSnapshot = true;

    // Resets/re-subscribes on workspace switch (workspaceFilter is in the
    // dep array below).
    const q = query(
      collection(services.db, 'concepts'),
      ...conceptsListScope(workspaceFilter, userId, source, teamScope),
      orderBy(CONCEPTS_LIST_SORT_FIELD, 'desc'),
      limit(pageSize),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const conceptsList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Concept[];

        if (!isInitialSnapshot) {
          for (const change of snapshot.docChanges()) {
            if (change.type !== 'added' && change.type !== 'modified') continue;
            const c = { id: change.doc.id, ...change.doc.data() } as Concept;
            if (getDisplayState(c) === 'ERROR' && !reportedErrors.has(c.id)) {
              reportedErrors.add(c.id);
              sendExceptionToSentry({
                error: new Error(`Concept generation failed: ${c.id}`),
                contextName: 'concept_error',
                context: { conceptId: c.id, name: c.current?.name },
                tags: { concept_id: c.id },
              });
            }
          }
        }
        isInitialSnapshot = false;

        setFirstPage(conceptsList);

        const tail = conceptsList[conceptsList.length - 1];
        firstPageTailCursorRef.current = tail ? readUpdatedAtCursor(tail) : null;

        // hasMore can only be ruled out before paging starts; once we've
        // paged past page 1, page-1 shrinking doesn't imply the end.
        if (!pagedTailCursorRef.current && conceptsList.length < pageSize) {
          setHasMore(false);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error fetching concepts:', err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
    // Intentionally exclude `paged` from deps: it would tear down the
    // listener on every loadMore call. `workspaceFilter` IS included so a
    // workspace switch resets page-1 + re-subscribes with the right query.
  }, [userId, includeArchived, pageSize, workspaceFilter, source, teamScope]);

  const loadMore = useCallback(async () => {
    const services = getFirebaseServices();
    if (!services || !userId || teamScope === undefined) return;
    if (loadingMore || !hasMore) return;
    const cursor =
      pagedTailCursorRef.current ?? firstPageTailCursorRef.current;
    // No cursor with hasMore still true means we can never page (e.g. the
    // page-1 listener errored, or a tail doc has no readable updated_at).
    // Flip hasMore off so fill-to-target consumers (the Add Existing
    // Concepts picker) don't retry forever.
    if (!cursor) {
      setHasMore(false);
      return;
    }

    setLoadingMore(true);
    try {
      const q = query(
        collection(services.db, 'concepts'),
        ...conceptsListScope(workspaceFilter, userId, source, teamScope),
        orderBy(CONCEPTS_LIST_SORT_FIELD, 'desc'),
        where(CONCEPTS_LIST_SORT_FIELD, '<', cursor),
        limit(pageSize),
      );
      const snapshot = await getDocs(q);
      const newPage = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Concept[];

      setPaged((prev) => concatDedupeById(prev, newPage));

      if (newPage.length > 0) {
        const tailCursor = readUpdatedAtCursor(newPage[newPage.length - 1]);
        if (tailCursor) pagedTailCursorRef.current = tailCursor;
      }

      setHasMore(newPage.length === pageSize);
    } catch (err) {
      console.error('Error loading more concepts:', err);
      setError(err as Error);
      // Stop paging on error: loadingMore flipping back to false re-runs the
      // consumers' fill/sentinel effects, and with hasMore still true they
      // would immediately retry the failing query in an infinite loop.
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, pageSize, loadingMore, hasMore, workspaceFilter, source, teamScope]);

  const concepts = useMemo(() => {
    const merged = mergePaginatedConcepts(firstPage, paged, includeArchived);
    // Filter by active workspace (Phase A.3.1). Done in the merge memo so
    // both live page-1 and paged remainders are scoped consistently. When
    // workspaces are gated, credits still route to the team wallet so a team
    // designer's new concepts are team-stamped — show all of the owner's
    // concepts regardless of stamp (pre-workspaces behavior). See useConcepts.
    if (!workspacesEnabled()) return merged;
    return merged.filter((c) => conceptBelongsToWorkspace(c, workspaceFilter));
  }, [firstPage, paged, includeArchived, workspaceFilter]);

  return { concepts, loading, loadingMore, hasMore, loadMore, error };
}

/**
 * Hook for a single concept with realtime updates.
 */
export function useConcept(conceptId: string | null) {
  const [concept, setConcept] = useState<Concept | null>(null);
  // The conceptId that `concept`/`error` currently reflect. It is updated ONLY
  // when a subscription resolves (snapshot, error, or the no-fetch branches).
  // `loading` is derived from it, so on the render right after `conceptId`
  // changes — before the effect re-subscribes — `resolvedId` still lags and
  // `loading` reads `true` synchronously. Keeping `loading` in useState instead
  // left a one-render gap where it reported a stale `false` from the previous
  // id, letting consumers act on not-yet-fetched data. That gap double-counted
  // the "Design Stream Start Over" analytics event on ?startFrom= remixes: the
  // draft fetch (gated on the missing-images condition) flipped on, but the
  // gate that waits for it saw the stale `false` and prefilled once (no images)
  // before the draft resolved and prefilled again (with images).
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setResolvedId(conceptId);
      return;
    }

    if (!conceptId) {
      setConcept(null);
      setError(null);
      setResolvedId(null);
      return;
    }

    setError(null);

    const conceptRef = doc(services.db, 'concepts', conceptId);

    const unsubscribe = onSnapshot(
      conceptRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setConcept({ id: snapshot.id, ...snapshot.data() } as Concept);
        } else {
          setConcept(null);
        }
        setResolvedId(conceptId);
      },
      (err) => {
        console.error('Error fetching concept:', err);
        setError(err);
        setConcept(null); // access lost: drop the stale copy
        setResolvedId(conceptId);
      }
    );

    return () => unsubscribe();
  }, [conceptId]);

  // `true` until the subscription for the CURRENT conceptId has resolved. This
  // is correct synchronously on the render where conceptId changes (resolvedId
  // still reflects the previous id), without waiting for the effect to run.
  const loading = resolvedId !== conceptId;

  return { concept, loading, error };
}

/**
 * Hook for concept versions (version history) with realtime updates.
 */
export function useConceptVersions(conceptId: string | null) {
  const { items, loading, error } = useConceptSubcollection<ConceptVersion>(
    conceptId,
    buildConceptVersionsQuery,
  );
  return { versions: items, loading, error };
}

/**
 * Hook for concept messages (chat thread) with realtime updates.
 */
export function useConceptMessages(conceptId: string | null) {
  const { items, loading, error } = useConceptSubcollection<Message>(
    conceptId,
    buildConceptMessagesQuery,
    normalizeRecentMessagesForDisplay,
  );
  return { messages: items, loading, error };
}

function buildConceptVideoArtifactsQuery(db: Firestore, conceptId: string) {
  const artifactsRef = collection(db, 'concepts', conceptId, 'artifacts');
  return query(artifactsRef, where('artifact_type', '==', 'VIDEO'));
}

function sortArtifactsDesc(items: Artifact[]): Artifact[] {
  return [...items].sort((a, b) => getTimestampMs(b.created_at) - getTimestampMs(a.created_at));
}

export function useConceptVideoArtifacts(conceptId: string | null) {
  const { items, loading, error } = useConceptSubcollection<Artifact>(
    conceptId,
    buildConceptVideoArtifactsQuery,
    sortArtifactsDesc,
  );
  return { artifacts: items, loading, error };
}

function buildConceptCloseupArtifactsQuery(db: Firestore, conceptId: string) {
  const artifactsRef = collection(db, 'concepts', conceptId, 'artifacts');
  return query(artifactsRef, where('artifact_type', '==', 'CLOSEUP'));
}

export function useConceptCloseupArtifacts(conceptId: string | null) {
  const { items, loading, error } = useConceptSubcollection<Artifact>(
    conceptId,
    buildConceptCloseupArtifactsQuery,
    sortArtifactsDesc,
  );
  return { artifacts: items, loading, error };
}

/**
 * Hook to fetch error messages for concepts in ERROR state.
 * Returns a map of conceptId -> error message text.
 */
export function useConceptErrorMessages(conceptIds: string[]) {
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services || conceptIds.length === 0) {
      setErrorMessages({});
      return;
    }

    // Set up listeners for each concept's messages
    const unsubscribes: (() => void)[] = [];

    for (const conceptId of conceptIds) {
      const q = buildConceptErrorMessagesQuery(services.db, conceptId);

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          // Find the latest assistant message with error content
          for (const doc of snapshot.docs) {
            const data = doc.data();
            if (data.role === 'assistant' && data.structured_output?.error) {
              const errorText = extractMessageOutputText(data.content);

              if (errorText) {
                setErrorMessages((prev) => ({ ...prev, [conceptId]: errorText }));
              }
              break;
            }
          }
        },
        (err) => {
          console.error(`Error fetching messages for concept ${conceptId}:`, err);
        }
      );

      unsubscribes.push(unsubscribe);
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [conceptIds.join(',')]); // Re-run when conceptIds change

  return errorMessages;
}

/**
 * Combined hook for concept detail view.
 * Composes the individual hooks for concept, versions, and messages.
 */
export function useConceptDetail(conceptId: string | null) {
  const { concept, loading: conceptLoading, error: conceptError } = useConcept(conceptId);
  const { versions, loading: versionsLoading, error: versionsError } = useConceptVersions(conceptId);
  const { messages, loading: messagesLoading, error: messagesError } = useConceptMessages(conceptId);

  return {
    concept,
    versions,
    messages,
    loading: conceptLoading || versionsLoading || messagesLoading,
    error: conceptError || versionsError || messagesError,
  };
}

/**
 * This client sends its ID token to /api/design-stream/chat. Tagging the user
 * PENDING message that starts a run says so, and the backend's tokenless
 * grace for older clients then never applies to that run.
 */
export const withChatTokenTag = (input: AddMessageInput): AddMessageInput =>
  input.message.role === 'user' && input.message.state === 'PENDING'
    ? {
        ...input,
        message: {
          ...input.message,
          structured_output: { ...input.message.structured_output, chat_token: true },
        },
      }
    : input;

/**
 * Hook for concept actions (add message, archive).
 * To create a new concept, use addMessage with concept_version but without concept_id/thread_id.
 */
export function useConceptActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Workspace context for credit deduction. addMessage stamps design_firm_id
  // from the active workspace at call time so the backend deducts from the
  // right wallet (Phase A.2a.1).
  const profile = useLoginState((s) => s.profile);
  const permissions = useLoginState((s) => s.permissions);
  const workspace = useWorkspaceContext(profile?.uid ?? null, permissions);

  const addMessage = useCallback(
    async (input: AddMessageInput): Promise<AddMessageResponse> => {
      const services = getFirebaseServices();
      if (!services) {
        throw new Error('Firebase not initialized');
      }

      const user = services.auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      setLoading(true);
      setError(null);
      try {
        // Get the user's ID token for authentication
        const idToken = await user.getIdToken();

        // Stamp the active workspace if the caller hasn't already set one.
        // Personal workspace → null; Team workspace → the firm id. Backend
        // validates membership and uses this to choose the wallet.
        const stamped: AddMessageInput =
          (input as any).design_firm_id !== undefined
            ? input
            : {
                ...input,
                ...({
                  design_firm_id:
                    workspace.active.kind === 'team'
                      ? workspace.active.firmId
                      : null,
                } as any),
              };

        // Use HTTP endpoint directly
        const response = await fetch(getAddMessageUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(withChatTokenTag(stamped)),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 402) {
            throw new Error(errorData.error || 'Insufficient credits');
          }
          throw new Error(errorData.error || `HTTP ${response.status}`);
        }

        return await response.json();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [workspace.active.id, workspace.active.kind]
  );

  // Associate (or clear, with null) the project a concept belongs to
  // (#3544 Phase 4). Metadata-only version: copies the current snapshot per
  // the metadata rule and overrides project_id.
  const setConceptProject = useCallback(
    async (
      concept: Concept,
      projectId: string | null,
    ): Promise<AddMessageResponse> => {
      const ver = concept.current;
      const res = await addMessage({
        concept_id: concept.id,
        message: {
          role: 'system',
          content: [
            {
              type: 'output_text' as const,
              text: projectId ? 'Added to project' : 'Removed from project',
            },
          ],
          state: 'COMPLETED',
        },
        concept_version: {
          img_url: ver?.img_url ?? null,
          action_type: 'METADATA_EDIT',
          ...copyVersionMetadata(ver),
          project_id: projectId,
        },
      });
      const action = fileActionOf(ver?.project_id, projectId);
      if (action) {
        teamAnalytics.itemFiled(teamItem('concept', concept.id, concept, 'editor'), {
          action,
          projectId,
        });
      }
      return res;
    },
    [addMessage]
  );

  const archiveConcept = useCallback(
    async (
      conceptId: string,
      currentImgUrl: string | null,
      currentName: string | null,
    ): Promise<AddMessageResponse> => {
      return addMessage({
        concept_id: conceptId,
        message: {
          role: 'system',
          content: [{ type: 'output_text' as const, text: 'Concept archived' }],
          state: 'COMPLETED',
        },
        concept_version: {
          name: currentName ?? 'Untitled Concept',
          img_url: currentImgUrl ?? null,
          action_type: 'ARCHIVE',
        },
      });
    },
    [addMessage]
  );

  // Undo for archiveConcept (#3792). Append-only, like every other write here:
  // a non-ARCHIVE version becomes `current`, and applyConceptArchiveFilter stops
  // excluding the concept.
  //
  // `priorVersion` must be the snapshot captured BEFORE archiving. archiveConcept
  // writes a version carrying only name + img_url, so restoring from the archived
  // `current` would null out narrative/tag/style/palette/project_id.
  const unarchiveConcept = useCallback(
    async (
      conceptId: string,
      priorVersion: ConceptVersion | null | undefined,
    ): Promise<AddMessageResponse> => {
      return addMessage({
        concept_id: conceptId,
        message: {
          role: 'system',
          content: [{ type: 'output_text' as const, text: 'Concept restored' }],
          state: 'COMPLETED',
        },
        concept_version: {
          img_url: priorVersion?.img_url ?? null,
          action_type: 'METADATA_EDIT',
          ...copyVersionMetadata(priorVersion),
        },
      });
    },
    [addMessage]
  );

  return {
    addMessage,
    archiveConcept,
    unarchiveConcept,
    setConceptProject,
    loading,
    error,
  };
}

/**
 * Backward-compatible wrapper: keep existing imports from useConcepts.ts intact.
 */
export function useCreateConcept() {
  const { addMessage } = useConceptActions();
  return useCreateConceptWithAddMessage(addMessage);
}
